import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import { fetchIntegrationsOverview } from './api'

/**
 * Cache keys for the Integrations overview.
 *
 * Its own root rather than a branch of `integrationKeys` or `collectorKeys`: the
 * rollup spans both, so hanging it off either would make that half's
 * invalidation drop a cache it only half owns — and leave the other half able to
 * change without refreshing it.
 */
export const integrationsOverviewKeys = {
	all: ['integrations-overview'] as const,

	/**
	 * Keyed by tenant even though the fixture ignores it — the real endpoint will
	 * be tenant-scoped, and a key that omits the tenant serves one tenant's estate
	 * to the next after a tenant switch.
	 */
	summary: (tenant: string) =>
		[...integrationsOverviewKeys.all, 'summary', tenant] as const,
}

export const integrationsOverviewQueries = {
	/**
	 * The tenant's cross-kind health rollup.
	 *
	 * A minute of `staleTime` rather than the Collectors list's thirty seconds:
	 * this page answers "is anything wrong?", not "is this agent alive right
	 * now" — the second-resolution liveness question belongs to the surface that
	 * shows one row per agent.
	 */
	summary: (tenant: string) =>
		queryOptions({
			queryKey: integrationsOverviewKeys.summary(tenant),
			queryFn: ({ signal }) => fetchIntegrationsOverview(signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(5, 'm'),
			// No `enabled` guard: the sole consumer is `useSuspenseQuery`, which
			// forces `enabled: true` and would silently ignore one. The tenant check
			// that matters is the route loader's, before it prefetches.
		}),
}

/** The Integrations overview for the current tenant. Suspends. */
export function useIntegrationsOverview() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(integrationsOverviewQueries.summary(tenant))
}
